/* YourXI Bespoke XI -> Shopify cart adapter.
 *
 * Loaded after the wizard script on the Shopify page (sections/yxi-bespoke-wizard.liquid).
 * Outside Shopify (Vercel, file://) it still loads, but YXIShop.inShopify is false.
 *
 * One line for the wizard's "Add to cart" handler:
 *   if (window.YXIShop && YXIShop.inShopify) return YXIShop.addFromWizard();
 *
 * Lower level:
 *   const blob = await YXIShop.renderPreviewPNG(document.querySelector("#printMount svg"));
 *   await YXIShop.addBespokeToCart(S, blob, YXIShop.variantIdFor(S.frame, S.size));
 */
(function () {
  "use strict";
  const CFG = window.YXI_SHOPIFY || null;              // printed by the section: {product, rebuildBase, cartUrl}
  const inShopify = !!(CFG && CFG.product && window.Shopify);
  const CHUNK = 1000;                                  // max chars per property value we rely on (see SHOPIFY_INTEGRATION.md)
  const ADAPTER_VERSION = "1";

  const SIZE_MAP = { "12x16": "12″×16″", "18x24": "18″×24″" };

  // £99 email offer (19 Oct 2026): CFG.offer99 holds the hidden offer products. Only used when the wizard passes offer:true.
  const offerOn = () => !!(CFG && CFG.offer99 && CFG.offer99.product);
  function variantIdFor(frame, size, offer) {
    const prod = offer && offerOn() ? CFG.offer99.product : CFG && CFG.product;
    if (!prod) return null;
    const want = [String(frame || "Black"), SIZE_MAP[size] || size];
    const v = prod.variants.find(v => v.option1 === want[0] && v.option2 === want[1]);
    return v ? v.id : null;
  }

  // ---------- compact code (same format as the wizard share link: "z" + base64url(deflate-raw(json))) ----------
  const b64u = bytes => { let s = ""; for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]); return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); };
  async function compact(obj) {
    const json = new TextEncoder().encode(JSON.stringify(obj));
    if (window.CompressionStream) {
      const cs = new Blob([json]).stream().pipeThrough(new CompressionStream("deflate-raw"));
      return "z" + b64u(new Uint8Array(await new Response(cs).arrayBuffer()));
    }
    return "j" + b64u(json);
  }
  async function shortRef(text) {
    const h = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text)));
    return "XI-" + Array.from(h.slice(0, 3), b => b.toString(16).padStart(2, "0")).join("").toUpperCase();
  }
  function chunk(props, key, value) {
    if (value.length <= CHUNK) { props[key] = value; return; }
    const n = Math.ceil(value.length / CHUNK);
    for (let i = 0; i < n; i++) props[`${key} ${i + 1}/${n}`] = value.slice(i * CHUNK, (i + 1) * CHUNK);
  }

  // ---------- readable lines (fallback if the wizard's lineItemProps() isn't available) ----------
  function fallbackProps(st) {
    const p = {};
    const line = x => !x ? "" : [[x.first, x.last].filter(Boolean).join(" ") || x.name,
      x.team ? `${x.team}${x.from || x.to ? " " + [x.from, x.to].filter(Boolean).join("-") : ""}` : "Club to be confirmed",
      `${({ home: "Home", away: "Away", third: "Third" })[x.side] || "Home"} kit`].join(", ");
    p["Title"] = (st.title || "").trim() || "Your All Time XI";
    p["Formation"] = st.formation;
    (st.slots || []).forEach((x, i) => { p[`${String(i + 1).padStart(2, "0")} ${i === 0 ? "Goalkeeper" : "Player"}`] = line(x) + (st.captain === i ? " (captain)" : ""); });
    (st.subs || []).forEach((x, i) => { if (x) p[`Sub ${i + 1}`] = line(x); });
    if ((st.manager || "").trim()) p["Manager"] = st.manager.trim();
    if ((st.message || "").trim()) p["Message"] = st.message.trim().replace(/\s*\n\s*/g, " / ");
    p["Frame"] = st.frame; p["Size"] = String(st.size || "").replace("x", " x ") + " inches";
    return p;
  }

  // ---------- preview PNG: inline fonts + blob images so the SVG rasterises exactly as on screen ----------
  const fontCache = {};
  async function fontCSS() {
    if (fontCache.css) return fontCache.css;
    const link = [...document.querySelectorAll('link[rel="stylesheet"]')].find(l => /fonts\.googleapis\.com/.test(l.href) && /Montserrat/.test(l.href));
    if (!link) return "";
    let css = await (await fetch(link.href)).text();
    // keep only Montserrat latin + latin-ext faces (the print font); drops Geist and other scripts
    css = (css.match(/@font-face\s*{[^}]*}/g) || []).filter(b => /Montserrat/.test(b) && (/U\+0000-00FF/i.test(b) || /U\+0100-02/i.test(b) || !/unicode-range/.test(b))).join("\n");
    const urls = [...new Set(css.match(/https:\/\/fonts\.gstatic\.com\/[^)'"]+/g) || [])];
    const data = await Promise.all(urls.map(async u => toDataURL(await (await fetch(u)).blob())));
    urls.forEach((u, i) => { css = css.split(u).join(data[i]); });
    return (fontCache.css = css);
  }
  const toDataURL = blob => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(blob); });

  async function renderPreviewPNG(svgEl, opts = {}) {
    if (!svgEl) throw new Error("renderPreviewPNG: no SVG element");
    const width = opts.width || 1800;
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
    const vb = (clone.getAttribute("viewBox") || "0 0 900 1200").split(/[\s,]+/).map(Number);
    const height = Math.round(width * vb[3] / vb[2]);
    clone.setAttribute("width", width); clone.setAttribute("height", height);
    for (const im of clone.querySelectorAll("image")) {                 // blob:/relative images -> data URLs
      const href = im.getAttribute("href") || im.getAttribute("xlink:href");
      if (href && !href.startsWith("data:")) { try { const d = await toDataURL(await (await fetch(href)).blob()); im.setAttribute("href", d); im.removeAttribute("xlink:href"); } catch (e) {} }
    }
    // the print relies on page CSS (.ptext font, .hit transparent, var(--print)); an SVG drawn as an image sees none of it,
    // so copy every readable page rule in, then freeze motion and UI-only states
    clone.querySelectorAll(".active,.drop-target,.armed,.dragging,.drop").forEach(el => el.classList.remove("active", "drop-target", "armed", "dragging", "drop"));
    let pageCSS = "";
    for (const sh of document.styleSheets) { try { for (const r of sh.cssRules) pageCSS += r.cssText + "\n"; } catch (e) {} }
    pageCSS += `\nsvg,svg.print{width:${width}px!important;height:${height}px!important}*{animation:none!important;transition:none!important}`;
    let fonts = ""; try { fonts = await fontCSS(); } catch (e) {}
    const st = document.createElementNS("http://www.w3.org/2000/svg", "style"); st.textContent = fonts + "\n" + pageCSS; clone.insertBefore(st, clone.firstChild);
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: "image/svg+xml" }));
    try {
      const img = new Image(); img.decoding = "sync";
      await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error("SVG did not rasterise")); img.src = url; });
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, width, height); ctx.drawImage(img, 0, 0, width, height);
      return await new Promise(res => c.toBlob(res, "image/png"));
    } finally { URL.revokeObjectURL(url); }
  }

  // ---------- build every property ----------
  async function buildProperties(state, opts = {}) {
    const props = Object.assign({}, opts.props || (typeof window.lineItemProps === "function" ? window.lineItemProps() : fallbackProps(state)));
    if (!props["Type"]) props["Type"] = state.mode === "custom" ? "Your own team" : "All-time legends";
    if (state.format && !props["Format"]) props["Format"] = /^\d+$/.test(String(state.format)) ? `${state.format}-a-side` : String(state.format);
    const pitch = state.pitch || state.pitchStyle;
    if (pitch && !Object.keys(props).some(k => /^pitch/i.test(k))) props["Pitch"] = typeof pitch === "string" ? pitch : JSON.stringify(pitch);
    const code = opts.code || (typeof window.encodeShare === "function" ? await window.encodeShare() : await compact(state));
    const ref = await shortRef(code);
    props["Design ref"] = ref;
    const base = (CFG && CFG.rebuildBase) || (location.origin + location.pathname);
    props["_Rebuild link"] = `${base}#xi=${code}`;
    const full = Object.assign({}, state); delete full.step;
    chunk(props, "_XI JSON", JSON.stringify(full));
    props["_Builder"] = `wizard state v${state.v || 1}, adapter v${ADAPTER_VERSION}, ${new Date().toISOString()}`;
    return { props, ref, code };
  }

  // ---------- add to cart (multipart, so files ride along as line item properties) ----------
  async function addBespokeToCart(state, previewBlob, variantId, opts = {}) {
    const offer = !!opts.offer && offerOn();
    variantId = variantId || variantIdFor(state.frame, state.size, offer);
    if (!variantId) throw new Error("No Shopify variant for " + state.frame + " / " + state.size);
    const { props, ref } = await buildProperties(state, opts);
    if (offer) props["_Offer"] = "99 email offer";
    const fd = new FormData();
    fd.append("id", String(variantId));
    fd.append("quantity", String(opts.quantity || 1));                  // the first copy; extra copies are their own line below
    for (const [k, v] of Object.entries(props)) if (v !== "" && v != null) fd.append(`properties[${k}]`, String(v));
    if (previewBlob) fd.append("properties[_Preview]", previewBlob, `${ref}-preview.png`);
    const photos = opts.photos || {};
    for (const [slot, url] of Object.entries(photos)) {                 // customer kit photos (object URLs)
      try { const b = await (await fetch(url)).blob(); fd.append(`properties[_Kit photo ${slot}]`, b, `${ref}-kit-${slot}.${(b.type.split("/")[1] || "jpg").replace("jpeg", "jpg")}`); } catch (e) {}
    }
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    const r = await fetch(root + "cart/add.js", { method: "POST", body: fd, headers: { Accept: "application/json" } });
    const out = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(out.description || out.message || ("cart/add failed " + r.status));
    // Extra copies of the same XI: "Your XI extra copy" (£59 / £89 by size), quantity = copies - 1, same design reference
    const extra = Math.max(0, (parseInt(opts.copies, 10) || 1) - 1);
    const sizeKey = /18/.test(String(opts.size || state.size || "")) ? "18x24" : "12x16";
    const copyCfg = offer && CFG.offer99.extraCopy && CFG.offer99.extraCopy[sizeKey] ? CFG.offer99.extraCopy : CFG && CFG.extraCopy;
    const copyVariant = copyCfg && copyCfg[sizeKey] && copyCfg[sizeKey].variantId;
    if (extra > 0) {
      if (!copyVariant) throw new Error("extra copy product missing");
      const cp = { "Design ref": ref, "Frame": String(state.frame || ""), "For": "Your XI " + ref + " (extra copies of the same XI)" };
      if (props["_Rebuild link"]) cp["_Rebuild link"] = props["_Rebuild link"];
      const c = await fetch(root + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: copyVariant, quantity: extra, properties: cp }] }) });
      if (!c.ok) { const e = await c.json().catch(() => ({})); throw new Error(e.description || e.message || ("extra copy cart/add failed " + c.status)); }
    }
    // Matchday set: the matching mug is its own hidden product (£20), a second line carrying the same design reference
    const mugVariant = CFG && CFG.mug && CFG.mug.variantId;
    if (opts.mug && mugVariant) {
      const mp = { "Design ref": ref, "For": "Your XI " + ref + " (Matchday set)" };
      if (props["_Rebuild link"]) mp["_Rebuild link"] = props["_Rebuild link"];
      const m = await fetch(root + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: mugVariant, quantity: 1, properties: mp }] }) });
      if (!m.ok) { const e = await m.json().catch(() => ({})); throw new Error(e.description || e.message || ("mug cart/add failed " + m.status)); }
    }
    return out;
  }

  // ---------- one-call entry point for the wizard ----------
  async function addFromWizard(opts = {}) {
    const st = opts.state || (typeof S !== "undefined" ? S : null);        // eslint-disable-line no-undef
    if (!st) throw new Error("addFromWizard: wizard state S not found");
    const btn = document.querySelector("#cartBtn");
    if (btn) { btn.disabled = true; btn.dataset.label = btn.textContent; btn.textContent = "Adding…"; }
    try {
      const svg = document.querySelector(opts.svgSelector || "#printMount svg");
      let blob = null;
      try { blob = (typeof window.getPreviewPNG === "function") ? await window.getPreviewPNG(1800) : await renderPreviewPNG(svg); }   // the wizard's own clean export first
      catch (e) { console.warn("[YXIShop] preview failed", e); try { blob = await renderPreviewPNG(svg); } catch (e2) {} }
      const ph = opts.photos || (typeof photos !== "undefined" ? photos : {}); // eslint-disable-line no-undef
      const item = await addBespokeToCart(st, blob, null, { photos: ph, quantity: opts.quantity, copies: opts.copies, size: opts.size, mug: !!opts.mug, offer: !!opts.offer });
      // builder marker for Clarity + customer events. Meta's AddToCart comes from Shopify's own product_added_to_cart (not doubled here).
      try { if (typeof window.yxiTrack === "function") window.yxiTrack("add_to_cart", { format: String(st.format || ""), mode: st.mode === "custom" ? "own" : "legends", size: String(opts.size || st.size || ""), copies: parseInt(opts.copies, 10) || 1, mug: !!opts.mug, offer99: !!opts.offer && offerOn(), currency: "GBP" }, { tags: [["builder_atc", "yes"]] }); } catch (e) {}
      if (opts.redirect !== false) await new Promise(r => setTimeout(r, 250));   // let the event leave before the page changes
      if (opts.redirect !== false) location.href = (CFG && CFG.cartUrl) || "/cart";
      return item;
    } catch (e) {
      if (btn) { btn.disabled = false; btn.textContent = btn.dataset.label || "Add to cart"; }
      if (typeof window.toast === "function") window.toast("Couldn't add to cart. Please try again."); else alert("Couldn't add to cart. Please try again.");
      throw e;
    }
  }

  window.YXIShop = { inShopify, config: CFG, variantIdFor, renderPreviewPNG, buildProperties, addBespokeToCart, addFromWizard, CHUNK };
})();
